import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, Database, Download, PlayCircle, CheckCircle2, AlertTriangle, Loader2, HardDrive, Mail, Wand2, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import CompletarCodigosFaltantes from "@/components/CompletarCodigosFaltantes";
import DesgloseAsistidoPanel from "@/components/DesgloseAsistidoPanel";
import LotesRepetidosPanel from "@/components/LotesRepetidosPanel";
import StockMinimoAutoPanel from "@/components/StockMinimoAutoPanel";

/**
 * SistemaPage — Panel de salud del sistema (solo director).
 * Muestra: estado de conexión con Mongo, conteos por colección,
 * backup más reciente, y permite ejecutar/descargar backups.
 */
export default function SistemaPage() {
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, b] = await Promise.all([
        api.get("/sistema/health/detallado"),
        api.get("/sistema/backup/lista"),
      ]);
      setHealth(h.data);
      setBackups(b.data || []);
    } catch (e) {
      toast.error("No se pudo cargar el estado del sistema");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const ejecutarBackup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/sistema/backup/ejecutar");
      toast.success(`Backup listo: ${data.backup.total_docs} documentos guardados`);
      load();
    } catch (e) {
      toast.error("Backup falló: " + (e?.response?.data?.detail || ""));
    } finally { setRunning(false); }
  };

  const descargarBackup = async () => {
    try {
      const token = localStorage.getItem("atlas_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/sistema/backup/descargar`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/gzip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `beem-backup-${new Date().toISOString().slice(0, 10)}.json.gz`;
      a.click();
      toast.success("Backup descargado. Guárdalo en un lugar seguro.");
    } catch (e) {
      toast.error("No se pudo descargar el backup");
    }
  };

  const enviarEmailBackup = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post("/sistema/backup/email");
      toast.success(`Backup enviado a ${data.to} · ${data.docs.toLocaleString("es-CO")} documentos`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`No se pudo enviar: ${detail}`);
    } finally { setSendingEmail(false); }
  };

  if (loading && !health) {
    return <div className="container mx-auto p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const mongoOk = health && !health.alertas?.some((a) => a.includes("Mongo"));

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6" data-testid="sistema-page">
      <header>
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Administración</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-2">
          <Shield className="w-8 h-8 text-primary" /> Salud del sistema
        </h1>
        <p className="text-muted-foreground mt-2">
          Estado en vivo del sistema, respaldos automáticos y herramientas de recuperación. Solo para el director.
        </p>
      </header>

      {/* Alertas */}
      {(health?.alertas || []).length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50">
          {health.alertas.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {a}
            </div>
          ))}
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Estado general */}
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Estado general</div>
          <div className="space-y-2 text-sm">
            <StatusRow label="API" status="ok" />
            <StatusRow label="MongoDB" status={mongoOk ? "ok" : "error"} />
            <StatusRow label="Chequeo automático" status="ok" hint="cada 60s" />
          </div>
        </Card>

        {/* Conteos */}
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Datos en el sistema</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            {Object.entries(health?.colecciones || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                <span className="tabular-nums font-medium">{v.toLocaleString("es-CO")}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Backups */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Respaldos automáticos</div>
            <div className="text-sm text-muted-foreground mt-1">
              Backup diario a las 3 AM (guarda 7 rodantes) · Email semanal cada domingo 6 AM a los directores.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={enviarEmailBackup} variant="outline" size="sm" data-testid="backup-email-btn" disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Enviarme por email
            </Button>
            <Button onClick={descargarBackup} variant="outline" size="sm" data-testid="backup-descargar-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar ahora
            </Button>
            <Button onClick={ejecutarBackup} disabled={running} size="sm" className="bg-primary hover:bg-primary/90" data-testid="backup-ejecutar-btn">
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              {running ? "Generando…" : "Crear backup"}
            </Button>
          </div>
        </div>

        {backups.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-md text-center">
            Aún no hay backups. Ejecuta "Crear backup" para hacer el primero.
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 text-left font-medium">Fecha</th>
                  <th className="p-2 text-right font-medium">Documentos</th>
                  <th className="p-2 text-right font-medium">Tamaño</th>
                  <th className="p-2 text-left font-medium">Archivo</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.key} className="border-t">
                    <td className="p-2">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="p-2 text-right tabular-nums">{(b.total_docs || 0).toLocaleString("es-CO")}</td>
                    <td className="p-2 text-right tabular-nums">{((b.size_bytes_gz || 0) / 1024).toFixed(1)} KB</td>
                    <td className="p-2 text-xs text-muted-foreground truncate max-w-xs">{b.key}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Guía de monitoreo externo */}
      <Card className="p-5 bg-primary/5 border-primary/20">
        <div className="flex items-center gap-2 mb-2 text-primary font-semibold">
          <HardDrive className="w-4 h-4" /> Monitoreo externo (recomendado)
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Configura un servicio gratuito como <b>UptimeRobot</b> o <b>BetterUptime</b> para recibir un email o WhatsApp
          si el sistema deja de responder por más de 3 minutos:
        </p>
        <div className="text-xs bg-background border rounded-md p-2 font-mono">
          URL a monitorear: <b>{process.env.REACT_APP_BACKEND_URL}/api/sistema/health</b>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Este endpoint es público (no requiere login) y responde <b>200 OK</b> cuando todo está bien, o <b>503</b> si Mongo falla.
        </p>
      </Card>

      {/* Backfill de Códigos Reales (Feb/2026) — solo aparece si el archivo del repo existe */}
      <BackfillCodigosCard />

      {/* Recovery EF26-03 (Oct/2026) — revierte fusión familia+color del contenedor CSGU7415170 */}
      <RecoveryEF26Card />

      {/* Completar Códigos Faltantes MANUAL (Feb/2026) — para productos que no
          están en ningún Excel de proveedor. Tabla inline + upload Excel/CSV. */}
      <StockMinimoAutoPanel />
      <DesgloseAsistidoPanel />
      <LotesRepetidosPanel />
      <CompletarCodigosFaltantes />
    </div>
  );
}

// ============================================================
// BackfillCodigosCard — completa "Código Real" (CLIENT'S REF)
// desde el archivo del repo `/app/backend/data/codigos_ef01_26.json`.
// Aplica también a la BD de producción tras el deploy.
// ============================================================
function BackfillCodigosCard() {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(null);
  const [showFullList, setShowFullList] = useState(false);

  const runDryRun = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await api.post("/admin/backfill-codigos?dry_run=true");
      setResult(data);
      if (data.bloqueado) {
        toast.error("Bloqueado: revisa las razones en el panel");
      } else {
        toast.success(`Dry-run OK: ${data.productos_a_actualizar} productos por actualizar`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al ejecutar dry-run");
    } finally { setLoading(false); }
  };

  const confirmar = async () => {
    if (!result || result.bloqueado) return;
    if (!window.confirm(`Aplicar ${result.productos_a_actualizar} cambios de código real. ¿Continuar?`)) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/backfill-codigos?dry_run=false");
      setApplied(data);
      toast.success(`Aplicado: ${data.productos_a_actualizar} productos, ${data.lotes_a_actualizar} lotes`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    } finally { setConfirming(false); }
  };

  const copyJSON = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    toast.success("JSON copiado al portapapeles");
  };

  return (
    <Card className="p-6 border-2 border-indigo-200 bg-indigo-50/30">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display text-xl flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-indigo-700" />
            Completar Códigos Reales
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Aplica el mapping SKU → Código Real (CLIENT'S REF) del archivo del repo a la BD actual.
            Deploy → correr aquí para llenar los códigos en producción.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runDryRun} disabled={loading} data-testid="backfill-codigos-dryrun">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            {loading ? "Analizando…" : "Dry-run"}
          </Button>
          {result && !result.bloqueado && (
            <Button
              onClick={copyJSON}
              variant="outline"
              size="sm"
              data-testid="backfill-codigos-copy"
              title="Copiar JSON completo al portapapeles"
            >
              <ClipboardCopy className="w-4 h-4 mr-1" /> Copiar JSON
            </Button>
          )}
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Contadores */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <Metric label="Archivo" value={result.pares_en_archivo} />
            <Metric label="Encontrados" value={result.productos_encontrados} />
            <Metric label="Por actualizar" value={result.productos_a_actualizar} highlight />
            <Metric label="Lotes" value={result.lotes_a_actualizar} />
            <Metric label="Ya correctos" value={result.productos_ya_correctos} />
            <Metric label="Sin match" value={result.skus_sin_match?.length || 0} />
            <Metric label="Duplicados" value={result.skus_duplicados_en_db?.length || 0} />
            <Metric label="Aplicado" value={applied?.aplicado ? "SÍ" : "no"} highlight={applied?.aplicado} />
          </div>

          {/* Bloqueo */}
          {result.bloqueado && (
            <div className="bg-rose-50 border border-rose-300 rounded-md p-3 text-sm text-rose-800" data-testid="backfill-codigos-bloqueado">
              <div className="font-bold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Bloqueado — no se puede aplicar</div>
              <ul className="list-disc pl-5 mt-1 text-xs">
                {result.razones_bloqueo?.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Tabla de cambios */}
          {result.cambios?.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 text-xs font-bold uppercase flex items-center justify-between">
                <span>Cambios a aplicar ({result.cambios.length})</span>
                <button
                  onClick={() => setShowFullList(!showFullList)}
                  className="text-indigo-700 hover:underline normal-case text-xs"
                  data-testid="backfill-codigos-toggle-list"
                >
                  {showFullList ? "Ver primeros 20" : `Ver los ${result.cambios.length}`}
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card border-b">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-1.5">SKU</th>
                      <th className="px-3 py-1.5">Nombre</th>
                      <th className="px-3 py-1.5">Código actual</th>
                      <th className="px-3 py-1.5">Código nuevo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showFullList ? result.cambios : result.cambios.slice(0, 20)).map((c) => {
                      const conflicto = c.codigo_anterior && c.codigo_anterior !== c.codigo_nuevo;
                      return (
                        <tr
                          key={c.id}
                          className={`border-b ${conflicto ? "bg-amber-50" : ""}`}
                          data-testid={`backfill-row-${c.sku}`}
                        >
                          <td className="px-3 py-1.5 font-mono">{c.sku}</td>
                          <td className="px-3 py-1.5 truncate max-w-[280px]" title={c.nombre}>{c.nombre}</td>
                          <td className={`px-3 py-1.5 font-mono ${conflicto ? "text-amber-700 font-bold" : "text-muted-foreground"}`}>
                            {c.codigo_anterior || "—"}
                          </td>
                          <td className="px-3 py-1.5 font-mono text-emerald-700 font-bold">{c.codigo_nuevo}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Listas auxiliares */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.skus_sin_match?.length > 0 && (
              <div className="border rounded-md p-2 bg-muted/20">
                <div className="text-xs font-bold uppercase text-muted-foreground mb-1">
                  SKUs sin match ({result.skus_sin_match.length})
                </div>
                <div className="max-h-32 overflow-y-auto text-xs font-mono text-muted-foreground">
                  {result.skus_sin_match.join(", ")}
                </div>
              </div>
            )}
            {result.skus_duplicados_en_db?.length > 0 && (
              <div className="border-2 border-rose-300 rounded-md p-2 bg-rose-50">
                <div className="text-xs font-bold uppercase text-rose-700 mb-1">
                  SKUs duplicados en BD ({result.skus_duplicados_en_db.length})
                </div>
                <div className="max-h-32 overflow-y-auto text-xs font-mono text-rose-800">
                  {result.skus_duplicados_en_db.join(", ")}
                </div>
              </div>
            )}
          </div>

          {/* Botón confirmar */}
          {!result.bloqueado && result.productos_a_actualizar > 0 && !applied?.aplicado && (
            <div className="flex justify-end pt-2 border-t">
              <Button
                onClick={confirmar}
                disabled={confirming}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="backfill-codigos-confirmar"
              >
                {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {confirming ? "Aplicando…" : `Confirmar y aplicar ${result.productos_a_actualizar} cambios`}
              </Button>
            </div>
          )}

          {applied?.aplicado && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-md p-3 text-sm text-emerald-800">
              <CheckCircle2 className="w-4 h-4 inline mr-1" />
              <b>Aplicado exitosamente.</b> {applied.productos_a_actualizar} productos y {applied.lotes_a_actualizar} lotes actualizados. Snapshot guardado en <code>backups</code>.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className={`rounded-md border p-2 ${highlight ? "bg-indigo-100 border-indigo-300" : "bg-card"}`}>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${highlight ? "text-indigo-800" : ""}`}>{value}</div>
    </div>
  );
}

function StatusRow({ label, status, hint }) {
  const ok = status === "ok";
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`flex items-center gap-1 text-xs font-medium ${ok ? "text-emerald-700" : "text-rose-700"}`}>
        {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {ok ? "Operativo" : "Falla"}
        {hint && <span className="text-muted-foreground ml-1">({hint})</span>}
      </span>
    </div>
  );
}


// ============================================================
// RecoveryEF26Card — revierte la fusión familia+color del contenedor
// CSGU7415170 (Oct/2026). Las 18 referencias GUIPIUR Blanco (EF26-01,
// 02, 04-09, 20, 30-38) quedaron absorbidas dentro de EF26-03.
// Este botón las recupera como productos independientes.
// Idempotente: si ya corrió, no duplica.
// ============================================================
function RecoveryEF26Card() {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [plan, setPlan] = useState(null);
  const [applied, setApplied] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await api.post("/admin/recovery/ef26-defusion?dry_run=true");
      setPlan(data);
      if (data.productos_a_crear === 0 && data.productos_ya_existen_saltados === 18) {
        toast.success("Ya está recuperado: los 18 productos existen como independientes");
      } else if (data.conflictos?.length > 0) {
        toast.error(`${data.conflictos.length} conflicto(s) sin resolver`);
      } else {
        toast.success(`Dry-run OK: ${data.productos_a_crear} productos por crear`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al ejecutar dry-run");
    } finally { setLoading(false); }
  };

  const confirmar = async () => {
    if (!plan || plan.productos_a_crear === 0) return;
    if (!window.confirm(
      `Esta operación va a:\n\n` +
      `• Crear ${plan.productos_a_crear} productos nuevos (EF26-01, 02, 04-09, 20, 30-38)\n` +
      `• Re-asignar los ${plan.productos_a_crear} lotes correspondientes de EF26-03 a esos nuevos productos\n` +
      `• Actualizar el stock de EF26-03 a 127 unidades (su lote original)\n` +
      `• Guardar backup completo en _recovery_backup_ef26 antes de escribir\n\n` +
      `Esta operación es idempotente — si vuelves a correrla no duplica. ¿Continuar?`
    )) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/recovery/ef26-defusion?dry_run=false");
      setApplied(data);
      toast.success(`✅ Recuperados: ${data.productos_creados} productos nuevos`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    } finally { setConfirming(false); }
  };

  return (
    <Card className="p-6 border-2 border-amber-200 bg-amber-50/30">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            Recuperar GUIPIUR del Contenedor CSGU7415170
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Revierte la fusión familia+color que absorbió 18 referencias GUIPIUR Blanco (EF26-01, 02, 04-09, 20, 30-38)
            dentro de EF26-03. Las deja como productos independientes con su código, SKU y stock original. Idempotente.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={runDryRun} disabled={loading} data-testid="recovery-ef26-dryrun">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            {loading ? "Analizando…" : "Ver plan"}
          </Button>
        </div>
      </div>

      {plan && (
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-white rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Lotes en EF26-03</div>
              <div className="text-2xl font-semibold">{plan.lotes_encontrados}</div>
            </div>
            <div className="bg-white rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Productos a crear</div>
              <div className="text-2xl font-semibold text-emerald-700">{plan.productos_a_crear}</div>
            </div>
            <div className="bg-white rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Ya existen (saltar)</div>
              <div className="text-2xl font-semibold text-muted-foreground">{plan.productos_ya_existen_saltados}</div>
            </div>
            <div className="bg-white rounded-md border p-3">
              <div className="text-xs text-muted-foreground">EF26-03 stock final</div>
              <div className="text-2xl font-semibold">{plan.superviviente_stock_final}</div>
            </div>
          </div>

          {plan.conflictos?.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900">
              <div className="font-semibold mb-1">Conflictos:</div>
              <ul className="list-disc list-inside">
                {plan.conflictos.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          {plan.productos_a_crear > 0 && (
            <div className="pt-2">
              <Button
                onClick={confirmar}
                disabled={confirming}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="recovery-ef26-apply"
              >
                {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {confirming ? "Aplicando…" : `Recuperar ${plan.productos_a_crear} productos`}
              </Button>
            </div>
          )}

          {plan.productos_a_crear === 0 && plan.productos_ya_existen_saltados === 18 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900">
              ✅ Ya está recuperado: los 18 productos existen como independientes. No hay nada que hacer.
            </div>
          )}
        </div>
      )}

      {applied && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm">
          <div className="font-semibold text-emerald-900">✅ {applied.mensaje}</div>
          <div className="mt-2 text-emerald-800">
            Productos creados: <b>{applied.productos_creados}</b> · Lotes reasignados: <b>{applied.lotes_reasignados}</b>
            <br />
            EF26-03 stock: {applied.superviviente_stock_antes} → <b>{applied.superviviente_stock_despues}</b>
            <br />
            Backup ID: <code className="text-xs">{applied.backup_id}</code>
          </div>
        </div>
      )}
    </Card>
  );
}
